# A route transition that dies on an aborted request must not leave a blank
# screen — card 6aab65a36c29c3a6dc3bbbb5.
#
# THE DEFECT. The play route resolves `studentRefresh` and `games` before it
# mounts anything (app-route.coffee). Those are network reads, and when one is
# aborted mid-flight ui-router rejects the whole transition. Nothing anywhere in
# el-feet registered an `onError` hook, so the rejection went to the router's
# default handler — a console line — and the UIView simply stayed empty.
# Measured on QA elfeet 2026-09-17, 1 of 46 play-route loads at 932x430: a solid
# grey frame for the full 120s budget, `Transition Rejection($id: 0 type: 6 …
# AxiosError: Request aborted)` in the console, the interpreter never mounted.
# The immediate rerun of the same node loaded in 10s — the request is transient,
# the grey screen is not: nothing recovers it but a manual refresh, and the
# person looking at it is a child.
#
# WHAT THIS DOES. One retry, then say something. A transient aborted XHR is
# exactly the thing a retry fixes, and the retry is free; if it fails a second
# time the connection is genuinely gone and the honest move is to tell the
# person rather than to keep trying behind a blank page.
#
# WHAT IT DELIBERATELY LEAVES ALONE. Only `type: 6` (ERROR) is ours. A
# SUPERSEDED or ABORTED rejection is the router's normal traffic — the child
# tapped something else while this one was resolving — and retrying it would
# drag them back to a screen they left.

# @uirouter/core RejectType. Written out rather than imported: this module is
# loaded by three shells and its only ui-router dependency is this one number.
ERROR_REJECTION = 6

# An aborted or dropped request, as axios reports it. Anything else (a 500, a
# bad param, a throw inside a resolve) is a real failure and retrying it just
# fails again more slowly.
RETRYABLE_CODES = ['ECONNABORTED', 'ERR_NETWORK', 'ERR_CANCELED']

# A GATEWAY SAYING "NOT RIGHT NOW" IS TRANSIENT TOO — card
# 6aaee726a822f303b055f86b.
#
# The predicate above admitted dropped connections and rethrew every HTTP
# status, so a 503 skipped the retry entirely and the family landed on the
# ErrorScreen — measured by boot-faults.spec.js with a 503 injected on only
# the FIRST getAccount, everything after it answering normally. These windows
# are real and short: 14 x 503 in 10 seconds on elephantlearning.com during a
# publish reload on 2026-09-19 (card 6aaec7926e4c9546748d6d4a), which is one
# ErrorScreen for every family who opened the app in those ten seconds.
#
# Only the three "the server is not taking requests right now" statuses, and
# 429. A 500 is the server failing at this particular work and will fail the
# same way again; a 4xx is us asking wrongly. Neither gets a retry.
RETRYABLE_STATUSES = [429, 502, 503, 504]

# EXPORTED because the same question is asked one step earlier. The app's boot
# read (`window.app.refresh()` in each shell's `initialize`) can be aborted by
# the same transient, and it happens BEFORE the router exists — so the hook
# below has not been installed yet and cannot help. Both places must agree on
# what "transient" means, or a request that is retried at the route is given up
# on at boot; one predicate, two callers (card 6aab772ced72ee7fceb166f3).
export isRetryable = (detail) ->
  return false unless detail?
  return true if detail.code? and detail.code in RETRYABLE_CODES
  # `status` is where a bare fetch/Response answer carries it; axios puts the
  # same number on `response.status` and reports the code as ERR_BAD_RESPONSE.
  status = detail.status ? detail.response?.status ? detail.statusCode
  return true if status? and Number(status) in RETRYABLE_STATUSES
  message = detail.message ? String(detail)
  /request aborted|network error|timeout/i.test message

# THE BOOT READ, one step before the router exists.
#
# Each shell's `initialize` opens with `window.app.refresh()` — the read that
# fetches the login and the account — and only then starts React and the
# router. When THAT read is aborted by the same transient, the hook above is not
# installed yet and cannot help: in /app the rejection reaches
# `render_react_error` and the person gets the full "We have encountered an
# error" apology page, and in the teacher and org shells there was no `.catch`
# at all, so the rejection went nowhere and the page simply stayed blank.
# Measured on QA elfeet 2026-09-17: 1 of 5 headless play-route loads at 932x430
# (card 6aab772ced72ee7fceb166f3).
#
# One retry, same judgment as the hook. A transient aborted XHR is what a retry
# fixes; if the second read fails too the connection is genuinely gone, and the
# caller's own error path — an apology page the person can act on — is the
# honest answer. Anything that is NOT a dropped request (a 500, a throw inside
# refresh) is rethrown untouched and reaches that path immediately, because
# retrying it just fails again more slowly.
#
# `attempt` is a function, not a promise: the retry has to issue a NEW request,
# and a promise can only be awaited again.
#
# THREE TRIES, BACKING OFF — card 6aaee726a822f303b055f86b. One immediate
# retry is the right answer to a dropped connection and the wrong one to a
# gateway window: a publish's 5xx burst lasts seconds, and two requests issued
# back to back both land inside it. 0.5s then 1s spends at most 1.5s before
# giving up and covers the bursts we have actually measured. The waits are
# BETWEEN attempts only — a read that succeeds first time costs nothing, which
# is every boot but the faulty ones.
BACKOFF_MS = [500, 1000]

# Injectable so the pin does not sleep; the app passes nothing.
export retryAbortedRead = (attempt, onRetry = null, deps = {}) ->
  wait = deps.wait ? (ms) -> new Promise (resolve) -> setTimeout resolve, ms
  backoff = deps.backoff ? BACKOFF_MS

  run = (tries) ->
    Promise.resolve()
      .then attempt
      .catch (error) ->
        throw error unless isRetryable error
        throw error if tries >= backoff.length
        onRetry? error, tries + 1
        Promise.resolve(wait backoff[tries]).then -> run tries + 1

  run 0

# `deps` exists so this is drivable without a browser (the pin supplies its own
# emit/report); the app passes nothing and gets the real toast and error track.
export installTransitionRecovery = (router, deps = {}) ->
  return false unless router?.transitionService?.onError?

  emit = deps.emit ? (message) -> window.app?.emitter?.emit? 'global toast', message, false
  report = deps.report ? (message) -> window.app?.trackError? message

  # One retry per target, not one retry ever: a second aborted load an hour
  # later on another node deserves its own retry.
  retried = {}

  router.transitionService.onError {}, (transition) ->
    rejection = transition?.error?()
    return unless rejection?.type is ERROR_REJECTION

    detail = rejection.detail
    target = transition.to?()
    name = target?.name ? target
    params = transition.params?() ? {}
    key = "#{name}:#{try JSON.stringify(params) catch then ''}"

    report "Route transition failed (#{name}): #{detail?.message ? rejection.message ? rejection}"

    unless isRetryable detail
      emit "Something went wrong opening this screen. Please reload the page."
      return

    if retried[key]
      # The retry failed too. Stop, and say so — a third attempt behind a blank
      # screen is indistinguishable from the defect this replaces.
      delete retried[key]
      emit "We could not load this screen. Please check your connection and reload the page."
      return

    retried[key] = true
    console.warn "Route transition to #{name} was aborted — retrying once"
    try
      p = router.stateService?.go name, params, {reload: true}
      p?.then?((-> delete retried[key]), (-> null))
    catch e
      delete retried[key]
      emit "We could not load this screen. Please check your connection and reload the page."
    return

  true
